import { useEffect, useRef, useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";

interface DocErrorPopoverProps {
  documentId: string;
  holdReason?: string | null;
  errorCode?: string | null;
  errorDetails?: unknown;
}

/**
 * WU-6 — deep-link template for the OCR service's log viewer. Set
 * `VITE_OCR_LOG_URL_TEMPLATE` at build time to e.g.
 * `https://ocr-pp.stratummanager.com/logs?documentId={documentId}`. When
 * unset, the "View OCR logs" affordance is hidden — preventing dead links
 * in dev or deploys that have no log viewer wired yet.
 */
const OCR_LOG_URL_TEMPLATE = import.meta.env["VITE_OCR_LOG_URL_TEMPLATE"] as
  | string
  | undefined;

function buildOcrLogUrl(documentId: string): string | null {
  if (!OCR_LOG_URL_TEMPLATE) return null;
  return OCR_LOG_URL_TEMPLATE.replace("{documentId}", encodeURIComponent(documentId));
}

/**
 * R-131 — small inline error trigger for documents in `status='error'`. The
 * `(i)` glyph sits next to the status badge in `BatchDetailPage`; clicking
 * it opens an absolutely-positioned popover with the human reason
 * (`holdReason`), the machine-readable `errorCode`, and a copy-to-clipboard
 * button for the full structured payload (the JSON body OCR returned).
 *
 * Why local component (vs Radix Popover): no `@radix-ui/react-popover` is
 * already in the bundle, and adding one for a single throwaway tooltip is
 * not worth the ~5KB. Outside-click + Escape close handlers are trivial.
 *
 * Click handlers are wrapped in `stopPropagation()` so the popover never
 * bubbles up to the row's `onClick={navigate(...)}` verify-jump.
 */
export function DocErrorPopover({
  documentId,
  holdReason,
  errorCode,
  errorDetails,
}: DocErrorPopoverProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const [copied, setCopied] = useState(false);
  const { hasPermission } = usePermissions();
  // WU-6 — only supervisors with `supervisor.ocr_logs` see the deep link,
  // AND only when the build was given an OCR_LOG_URL_TEMPLATE. Both gates
  // must pass; the link never shows in dev or to operators.
  const ocrLogUrl = hasPermission("supervisor.ocr_logs")
    ? buildOcrLogUrl(documentId)
    : null;

  // Outside-click + Escape close so the popover never strands itself.
  useEffect(() => {
    if (!open) return;
    function handleClick(event: MouseEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const copyPayload = (() => {
    if (errorDetails !== undefined && errorDetails !== null) {
      try {
        return JSON.stringify(errorDetails, null, 2);
      } catch {
        // fall through
      }
    }
    return [errorCode ? `code: ${errorCode}` : null, holdReason]
      .filter(Boolean)
      .join("\n");
  })();

  async function handleCopy(event: React.MouseEvent) {
    event.stopPropagation();
    try {
      await navigator.clipboard?.writeText?.(copyPayload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable (insecure context, jsdom). Swallow — the
      // popover text is still on-screen for manual selection.
    }
  }

  return (
    <span className="doc-error-info-wrap" ref={wrapRef}>
      <button
        type="button"
        className="doc-error-info-trigger"
        data-testid={`doc-error-info-${documentId}`}
        aria-label="Show error details"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {/* Lowercase `i` glyph in a circle — minimal, theme-neutral. */}
        i
      </button>
      {open && (
        <div
          className="doc-error-popover"
          data-testid={`doc-error-popover-${documentId}`}
          role="dialog"
          aria-label="Document error details"
          onClick={(event) => event.stopPropagation()}
        >
          {errorCode && (
            <div className="doc-error-popover-row">
              <span className="doc-error-popover-label">Code</span>
              <code className="doc-error-popover-code">{errorCode}</code>
            </div>
          )}
          {holdReason && (
            <div className="doc-error-popover-row">
              <span className="doc-error-popover-label">Reason</span>
              <span className="doc-error-popover-reason">{holdReason}</span>
            </div>
          )}
          <div className="doc-error-popover-actions">
            <button
              type="button"
              className="doc-error-popover-copy"
              onClick={handleCopy}
            >
              {copied ? "Copied" : "Copy"}
            </button>
            {ocrLogUrl && (
              <a
                className="doc-error-popover-logs"
                href={ocrLogUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`doc-error-popover-ocr-logs-${documentId}`}
                onClick={(e) => e.stopPropagation()}
              >
                View OCR logs ↗
              </a>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
